package app.alefly.core.deeplink

sealed interface AleflyRoute {
    data object Home : AleflyRoute
    data object AllTenants : AleflyRoute
    data object Quote : AleflyRoute
    data object ChallengeAppProduct : AleflyRoute
    data object QuizEngineProduct : AleflyRoute
    data object Terms : AleflyRoute
    data object Privacy : AleflyRoute
    data object About : AleflyRoute
    data object Contact : AleflyRoute
    data class QuizCategory(val category: String) : AleflyRoute
    data class TenantLanding(val tenantId: String) : AleflyRoute
    data class TenantChallenges(val tenantId: String) : AleflyRoute
    data class ChallengePage(val tenantId: String, val challengeId: String) : AleflyRoute
    data class ChallengeResult(val tenantId: String, val challengeId: String) : AleflyRoute
    data class QuestionPage(val tenantId: String, val questionSlug: String) : AleflyRoute
    data class ThemePage(val tenantId: String, val tag: String) : AleflyRoute
}

object AleflyRouteParser {
    fun parse(pathname: String): AleflyRoute {
        val normalizedPathname = normalizePathname(pathname)

        val parts = normalizedPathname
            .trim()
            .trim('/')
            .split('/')
            .filter(String::isNotBlank)

        return when (parts.size) {
            0 -> AleflyRoute.Home
            1 -> parseSingleSegment(parts[0])
            2 -> parseTwoSegments(parts)
            3 -> parseThreeSegments(parts)
            4 -> parseFourSegments(parts)
            else -> AleflyRoute.Home
        }
    }

    private fun parseSingleSegment(segment: String): AleflyRoute = when (segment) {
        "quizzes", "tenants" -> AleflyRoute.AllTenants
        "orcamento" -> AleflyRoute.Quote
        "challenge-app" -> AleflyRoute.ChallengeAppProduct
        "quiz-engine" -> AleflyRoute.QuizEngineProduct
        "termos" -> AleflyRoute.Terms
        "privacidade" -> AleflyRoute.Privacy
        "sobre" -> AleflyRoute.About
        "contato" -> AleflyRoute.Contact
        else -> AleflyRoute.TenantLanding(segment)
    }

    private fun parseTwoSegments(parts: List<String>): AleflyRoute = when (parts[0]) {
        "quizzes" -> sanitizeSlug(parts[1]).let { category ->
            if (category.isNotEmpty()) AleflyRoute.QuizCategory(category) else AleflyRoute.AllTenants
        }
        else -> if (parts[1] == "desafios") AleflyRoute.TenantChallenges(parts[0]) else AleflyRoute.Home
    }

    private fun parseThreeSegments(parts: List<String>): AleflyRoute = when (parts[1]) {
        "q" -> sanitizeSlug(parts[2]).let { slug ->
            if (slug.isNotEmpty()) AleflyRoute.QuestionPage(parts[0], slug) else AleflyRoute.TenantLanding(parts[0])
        }
        "t" -> sanitizeSlug(parts[2]).let { tag ->
            if (tag.isNotEmpty()) AleflyRoute.ThemePage(parts[0], tag) else AleflyRoute.TenantLanding(parts[0])
        }
        "c" -> AleflyRoute.ChallengePage(parts[0], normalizeChallengeId(parts[2]))
        else -> AleflyRoute.Home
    }

    private fun parseFourSegments(parts: List<String>): AleflyRoute =
        if (parts[1] == "c" && parts[3] == "r") AleflyRoute.ChallengeResult(parts[0], normalizeChallengeId(parts[2]))
        else AleflyRoute.Home

    fun normalizeChallengeId(rawValue: String): String {
        return rawValue.substringBefore(' ').trim()
    }

    private val SAFE_SLUG = Regex("^[a-z0-9][a-z0-9_-]*$")

    fun sanitizeSlug(rawValue: String): String {
        val trimmed = rawValue.trim().lowercase()
        return if (SAFE_SLUG.matches(trimmed)) trimmed else ""
    }

    private fun normalizePathname(pathname: String): String {
        val normalizedSeparator = pathname.replace('\\', '/')
        if (normalizedSeparator.endsWith("/index.html")) {
            val withoutIndex = normalizedSeparator.removeSuffix("/index.html")
            return withoutIndex.ifEmpty { "/" }
        }
        return pathname
    }
}
